import {
    NCoreUIKitToast
} from "ncore-ui-kit";
import {
    type ActionResponseTypes,
    type ServiceContextType,
    type ActionRequestTypes,
    type ActionsType,
    type ServiceType
} from "../types";
import AsyncStorage from "@react-native-async-storage/async-storage";
import NContext, {
    type ConfigType
} from "ncore-context";
import moment from "moment";
import {
    SERVICE_EVENT_TYPES,
    SERVICE_CONFIGS
} from "../constants";
import actions from "../actions";

class ServiceContextInheritence extends NContext<ServiceContextType, ConfigType<ServiceContextType>> {
    tokenRefreshDate: Date = moment(new Date()).add(-100, "day").toDate();
    isTokenRefreshing = false;
    actions;
    queue: Array<{
        actionName: ActionsType;
        params: unknown;
        queueId: string;
    }> = [];

    constructor(initialState: ServiceType) {
        super(
            initialState,
            {
                key: "service-context"
            }
        );

        this.actions = initialState.actions;
    }

    emitWithKey = (key: string, payload?: unknown) => {
        this.subscribers.forEach(subscriber => {
            if (subscriber.key === key) subscriber.func(payload, this.state);
        });
    };

    removeAllQueueListeners = () => {
        this.queue.forEach((queueItem) => {
            this.removeEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueItem.queueId}`);
            this.removeEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueItem.queueId}-error`);
        });
    };

    startRefreshToken = () => {
        this.isTokenRefreshing = true;

        this.actions.refreshToken(null)
            .then((res) => {
                this.isTokenRefreshing = false;

                if (res?.payload?.userInfo) {
                    this.emitWithKey(SERVICE_EVENT_TYPES.USER_UPDATED, res.payload.userInfo);
                }

                this.queue.forEach(queueItem => {
                    this.emitWithKey(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueItem.queueId}`, true);
                    this.emitWithKey("REFRESH_TOKEN_TRIGGER");
                });
                this.queue = [];
            })
            .catch(() => {
                this.isTokenRefreshing = false;

                this.queue.forEach(queueItem => {
                    this.emitWithKey(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueItem.queueId}-error`, false);
                });
                this.removeAllQueueListeners();
                this.queue = [];
            });
    };

    addQueue = <K extends ActionsType>(actionName: K, params: ActionRequestTypes[K]) => {
        if (!this.isTokenRefreshing) {
            this.startRefreshToken();
        }

        return new Promise((resolve, reject) => {
            const queueId = Math.random().toString(36).substring(2, 15);

            let timeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
                reject(false);
            }, 30000);

            this.queue.push({
                actionName: actionName,
                queueId: queueId,
                params: params
            });

            const successQueueTriggered = () => {
                this.tokenRefreshDate = new Date();
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }

                this.removeEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueId}`);
                this.removeEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueId}-error`);

                resolve(true);
            };

            const errorQueueTriggered = () => {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }

                reject(false);
            };

            this.addEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueId}`, successQueueTriggered);
            this.addEventListener(`${SERVICE_EVENT_TYPES.REFRESH_TOKEN}-${queueId}-error`, errorQueueTriggered);
        });
    };

    action = async <K extends ActionsType>(actionName: K, params: ActionRequestTypes[K]): Promise<ActionResponseTypes[K]> => {
        // actionName is dynamic and maps to actions
        return await this.actions[actionName](params)
            .then((res: ActionResponseTypes[K]) => {
                if(!(res.code === 200 || res.code === 201 || res.code === 202)){
                    NCoreUIKitToast.open({
                        title: ("message" in res ? res.message : "Error") as string,
                        type: "warning"
                    });
                }
                return res;
            })
            .catch(async (err: unknown) => {
                const error = err as { code?: number; status?: number; payload?: { isMaintenance?: boolean }; message?: string };
                console.log("action err", " action name:", actionName, " params: ", params, " err:", error);
                const isExpiredToken = error.message === "expired-token" || error.message?.includes("expired") || error.message?.includes("token");
                const isUnauthorized = error.code === 401 || error.status === 401 || isExpiredToken;

                if (!(actionName === "threeTGetEventRound" && (error.code === 404 || error.status === 404))) {
                    if (!isUnauthorized) {
                        NCoreUIKitToast.open({
                            title: error.message || "Bilinmeyen Hata",
                            type: "danger"
                        });
                    }
                }
                if (!SERVICE_CONFIGS[actionName].isWorkedWithQueue) {
                    throw err;
                }

                if ((error.code === 503 || error.status === 503) && error.payload?.isMaintenance) {
                    this.emitWithKey("MAINTENANCE_MODE", true);
                }

                if (isUnauthorized) {
                    if(moment().diff(moment(this.tokenRefreshDate), "seconds") < 10){
                        // actionName is dynamic and maps to actions
                        return await this.actions[actionName](params);
                    }

                    return await this.addQueue(actionName, params)
                        .then(async () => {
                            // actionName is dynamic and maps to actions
                            return await this.actions[actionName](params);
                        })
                        .catch(() => {
                            this.emitWithKey(SERVICE_EVENT_TYPES.GO_TO_LOGOUT, true);
                            this.removeAllQueueListeners();
                            this.queue = [];

                            AsyncStorage.removeItem("authToken");
                            AsyncStorage.removeItem("authRefreshToken");

                            throw {
                                message: "session-ended"
                            };
                        });
                }

                throw err;
            });
    };
};

const RESTServiceContext = new ServiceContextInheritence({
    actions: actions
});

export default RESTServiceContext;
